import React, { useMemo, useRef } from "react";
import * as THREE from "three";
import { useFrame } from "@react-three/fiber";

/*
 * Elegant dark silhouettes of the wider portfolio (beer, gin, tequila,
 * scotch, vodka, liqueur). The wine bottle remains the hero — these read
 * as depth shapes only, fading in during the portfolio chapter.
 */
function makeProfile({ r, bodyH, shoulderH, neckR, neckH, lip = 0.02 }) {
  const pts = [
    [0.001, 0.02],
    [r * 0.7, 0.02],
    [r, 0.06],
    [r, bodyH],
    [r * 0.86, bodyH + shoulderH * 0.45],
    [neckR * 1.6, bodyH + shoulderH * 0.85],
    [neckR, bodyH + shoulderH],
    [neckR, bodyH + shoulderH + neckH],
    [neckR + lip, bodyH + shoulderH + neckH + 0.03],
    [neckR * 0.7, bodyH + shoulderH + neckH + 0.035],
  ];
  const curve = new THREE.SplineCurve(pts.map(([x, y]) => new THREE.Vector2(x, y)));
  return new THREE.LatheGeometry(curve.getPoints(60), 40);
}

const SHAPES = [
  // beer — short, stout
  { params: { r: 0.3, bodyH: 1.05, shoulderH: 0.4, neckR: 0.1, neckH: 0.45 }, pos: [-2.7, 0, -0.7], rot: 0.4 },
  // gin — tall, straight-shouldered
  { params: { r: 0.34, bodyH: 1.7, shoulderH: 0.25, neckR: 0.11, neckH: 0.5 }, pos: [-2.2, 0, 0.35], rot: 1.2 },
  // tequila — compact
  { params: { r: 0.36, bodyH: 0.85, shoulderH: 0.35, neckR: 0.11, neckH: 0.5 }, pos: [-0.72, 0, -0.85], rot: 2.1 },
  // scotch — broad round shoulder
  { params: { r: 0.4, bodyH: 1.35, shoulderH: 0.55, neckR: 0.12, neckH: 0.55 }, pos: [-0.28, 0, 0.5], rot: 0.9 },
  // vodka — tall sleek
  { params: { r: 0.3, bodyH: 1.9, shoulderH: 0.5, neckR: 0.1, neckH: 0.35 }, pos: [0.42, 0, -0.55], rot: 1.7 },
  // liqueur — slender, long neck
  { params: { r: 0.26, bodyH: 1.2, shoulderH: 0.35, neckR: 0.085, neckH: 0.85 }, pos: [0.95, 0, 0.25], rot: 2.6 },
];

export const SilhouetteBottles = ({ opacityRef }) => {
  const groupRef = useRef();
  const materials = useMemo(
    () =>
      SHAPES.map(
        () =>
          new THREE.MeshStandardMaterial({
            color: "#0b0b0b",
            roughness: 0.42,
            metalness: 0.15,
            transparent: true,
            opacity: 0,
            envMapIntensity: 0.7,
          })
      ),
    []
  );
  const geoms = useMemo(() => SHAPES.map((s) => makeProfile(s.params)), []);

  useFrame(() => {
    const o = opacityRef.current || 0;
    if (groupRef.current) groupRef.current.visible = o > 0.02;
    materials.forEach((m) => {
      m.opacity = o;
    });
  });

  return (
    <group ref={groupRef} visible={false}>
      {SHAPES.map((s, i) => (
        <mesh key={i} geometry={geoms[i]} material={materials[i]} position={s.pos} rotation-y={s.rot} />
      ))}
    </group>
  );
};
